import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { NewEditClass } from '../../../../classes/new-edit.class';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { PopupViewService } from '../../../../shared/components/popup-view/popup-view.service';
import { ApiValidatorService } from '../../../../core/services/api-validator.service';
import { RouterHelperService } from '../../../../core/services/router-helper.service';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { EMatchLevelZoom, IGeocodedSuggestion, MapService } from '../../../../shared/modules/map/map.service';
import { ZONE_DEFAULT_COLOR, ZonesService } from '../../../dashboard/zones/zones.service';
import { Zone } from 'src/app/interfaces/zone';
import { tap } from 'rxjs/operators';
import { Area } from '../../../../interfaces/poi';
import { PERMISSIONS, PermissionService } from '../../../../core/services/permission.service';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import {
  ZONE_BOUNDS_QUERY_PARAM,
  ZONE_EDIT_QUERY_PARAM
} from '../../../../shared/modules/map/map-zone-contextmenu/map-zone-contextmenu.component';

// popup enter animation (0.6s) and width transition change the map container size after the map is created
const INITIAL_VIEW_PERIOD = 1500;

@Component({
  selector: 'app-zone-details',
  templateUrl: './zone-basic.component.html',
  styleUrls: ['./zone-basic.component.scss'],
  providers: [MapService],
})
export class ZoneBasicComponent extends NewEditClass implements AfterViewInit {
  @ViewChild('mapContainer', {static: true}) mapContainer: ElementRef<HTMLElement>;

  public zone: Zone;
  public area: Area;
  protected editPermission = PERMISSIONS.ZONE_UPDATE;
  protected newPermission = PERMISSIONS.ZONE_CREATE;

  public toolboxIcons = [
    {icon: 'icon-search', value: 'search'},
  ];
  public activeIcon = {};
  public geocodedSuggestion: IGeocodedSuggestion = null;
  public lockMove = true;
  public initialBounds: H.geo.Rect = null;
  private resizeObserver: ResizeObserver;
  private mapLoadedAt: number = null;

  constructor(private route: ActivatedRoute,
              popupService: PopupViewService,
              apiValidator: ApiValidatorService,
              private mapService: MapService,
              private routerHelper: RouterHelperService,
              private service: ZonesService,
              permissio: PermissionService,
              fb: UntypedFormBuilder,
              private translate: TranslateService,
              private router: Router,
              modal: NgbModal
              ) {
    super(popupService, apiValidator, fb, permissio, modal);
  }

  ngOnInit() {
    super.ngOnInit();
    this.applyQueryParams();
    this.subscriptions.add(this.mapService.mapIsLoad.subscribe(() => {
      this.mapLoadedAt = Date.now();
      this.applyInitialBounds();
    }));
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      if (this.mapLoadedAt === null) {
        return;
      }
      this.mapService.resizeMap();
      if (Date.now() - this.mapLoadedAt < INITIAL_VIEW_PERIOD) {
        this.applyInitialBounds();
      }
    });
    this.resizeObserver.observe(this.mapContainer.nativeElement);
  }

  applyInitialBounds() {
    if (this.initialBounds) {
      this.mapService.setViewBounds(this.initialBounds);
    }
  }

  destroyFn() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  applyQueryParams() {
    const params: Params = this.route.snapshot.queryParams;
    if (!params[ZONE_EDIT_QUERY_PARAM] && !params[ZONE_BOUNDS_QUERY_PARAM]) {
      return;
    }
    if (!this.isNewView && params[ZONE_EDIT_QUERY_PARAM] === 'true' && this.showButton && !this.isEditMode) {
      this.isEditMode = true;
      this.toggleDisableForm();
    }
    this.initialBounds = this.parseBounds(params[ZONE_BOUNDS_QUERY_PARAM]);
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {[ZONE_EDIT_QUERY_PARAM]: null, [ZONE_BOUNDS_QUERY_PARAM]: null},
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  parseBounds(value: string): H.geo.Rect {
    if (!value) {
      return null;
    }
    const coords: number[] = value.split(',').map(Number);
    const [top, left, bottom, right] = coords;
    const isValid: boolean = coords.length === 4 && coords.every((coord: number) => isFinite(coord))
      && Math.abs(top) <= 90 && Math.abs(bottom) <= 90 && top > bottom
      && Math.abs(left) <= 180 && Math.abs(right) <= 180;
    return isValid ? new H.geo.Rect(top, left, bottom, right) : null;
  }

  toggleToolbox($event) {
    this.activeIcon = $event;
  }

  get showSearchResults() {
    return this.activeIcon.hasOwnProperty('search');
  }

  suggestionSelected(geo: IGeocodedSuggestion) {
    this.geocodedSuggestion = geo;
    this.mapService.zoomToCluster(
      [geo.coordinates.lng, geo.coordinates.lat],
      EMatchLevelZoom[geo.matchLevel] ? EMatchLevelZoom[geo.matchLevel] : 16
    );
  }

  deleteZone() {
    this.deleteElement(this.zone.id, {
      header: 'ZONE',
      body: this.translate.instant('DELETE_PROMPT', { name: this.zone.name})
    },
      (id) => this.service.delete(id)
    ).then(res => {
      // this.getCollection();
      this.popupService.close();
    }, () => {

    });
  }





  addRouteSub() {
    this.subscriptions.add(this.route.parent.data.subscribe(({zone}) => {
      this.zone = zone;
      this.area = this.zone.area;
      this.resetForm();
    }));
  }

  resetForm() {
    this.forms.main.patchValue(Object.assign({color: ZONE_DEFAULT_COLOR}, this.zone));
    this.zone.area = {...this.zone.area};
  }

  get color() {
    return this.forms.main.get('color').value;
  }

  buildForm() {
    this.forms = {
      main: this.fb.group({
        name: [null, Validators.required],
        area: [null, Validators.required],
        color: [null, Validators.required],
      })
    };
  }

  save() {
    this.service.add(this.forms.main.value).subscribe((res) => {
      this.pending = false;
      this.routerHelper.navigatePopup(['zone', res.id]);
    }, this.errorHandler.bind(this));
  }

  areaChange(area) {
    this.forms.main.get('area').setValue(area);
    this.forms.main.get('area').markAsDirty();
  }

  update() {
    this.service.partialUpdate(this.zone.id, this.forms.main.value).pipe(
      tap(res => Object.assign(this.zone, res)),
    ).subscribe(res => {
      this.pending = false;
      this.isEditMode = false;
      this.toggleDisableForm();
    }, this.errorHandler.bind(this));
  }
}
